import EmptyState from '@/components/EmptyState';
import { useT } from '@/i18n';
import type { Translations } from '@/i18n/locales/en';
import type { TableSessionTimelineEntry } from '@restaurant/api-client';
import { Block, Navbar, Page, Preloader } from 'framework7-react';
import { DateTime } from 'luxon';
import { useTableSession } from '../hooks/useTableOrdering';

type Kind = TableSessionTimelineEntry['kind'];
type TimelineStrings = Translations['tableOrdering']['timeline'];
type TimelineLabelKey = Exclude<
  keyof TimelineStrings,
  'activity' | 'noActivity' | 'noActivityDesc'
>;

// One row's icon + tone, grouped so the visit reads at a glance - lifecycle
// (open/close), kitchen (fired→served), money (bill/pay), and the exceptional
// in red (cancel/void/force-close). Text labels live in i18n (`timeline.*`) -
// `KIND_LABEL_KEY` below maps each kind to its key so this stays colour-only.
const KIND_META: Record<Kind, { icon: string; color: string; tint: string }> = {
  OPENED: { icon: 'flag', color: '#34c759', tint: 'rgba(52,199,89,0.14)' },
  ROUND_PLACED: { icon: 'doc_text', color: '#ff9500', tint: 'rgba(255,149,0,0.14)' },
  ROUND_FIRED: { icon: 'flame', color: '#007aff', tint: 'rgba(0,122,255,0.14)' },
  ROUND_READY: { icon: 'bell', color: '#34c759', tint: 'rgba(52,199,89,0.14)' },
  ROUND_SERVED: { icon: 'checkmark_alt', color: '#8e8e93', tint: 'rgba(142,142,147,0.16)' },
  ROUND_CANCELLED: { icon: 'xmark', color: '#ff3b30', tint: 'rgba(255,59,48,0.14)' },
  ITEM_VOIDED: { icon: 'trash', color: '#ff3b30', tint: 'rgba(255,59,48,0.14)' },
  ITEM_RESTORED: { icon: 'arrow_counterclockwise', color: '#34c759', tint: 'rgba(52,199,89,0.14)' },
  RESERVATION_ATTACHED: { icon: 'link', color: '#5856d6', tint: 'rgba(88,86,214,0.14)' },
  SERVICE_REQUESTED: { icon: 'bell_fill', color: '#ff9500', tint: 'rgba(255,149,0,0.14)' },
  SERVICE_RESOLVED: { icon: 'checkmark', color: '#34c759', tint: 'rgba(52,199,89,0.14)' },
  BILL_REQUESTED: { icon: 'doc_plaintext', color: '#ff9500', tint: 'rgba(255,149,0,0.14)' },
  PAYMENT_TAKEN: { icon: 'creditcard', color: '#34c759', tint: 'rgba(52,199,89,0.14)' },
  FORCE_CLOSED: {
    icon: 'exclamationmark_triangle',
    color: '#ff3b30',
    tint: 'rgba(255,59,48,0.14)',
  },
  MOVED: { icon: 'arrow_right_arrow_left', color: '#5856d6', tint: 'rgba(88,86,214,0.14)' },
  MERGED_INTO: { icon: 'arrow_merge', color: '#5856d6', tint: 'rgba(88,86,214,0.14)' },
  CLOSED: { icon: 'lock', color: '#8e8e93', tint: 'rgba(142,142,147,0.16)' },
  SEATED_FROM_RESERVATION: {
    icon: 'person_badge_plus',
    color: '#5856d6',
    tint: 'rgba(88,86,214,0.14)',
  },
};

/** Maps a timeline entry's kind to its `timeline.*` i18n key. */
const KIND_LABEL_KEY: Record<Kind, TimelineLabelKey> = {
  OPENED: 'tabOpened',
  ROUND_PLACED: 'roundPlaced',
  ROUND_FIRED: 'sentKitchen',
  ROUND_READY: 'roundReady',
  ROUND_SERVED: 'roundServed',
  ROUND_CANCELLED: 'roundCancelled',
  ITEM_VOIDED: 'itemVoided',
  ITEM_RESTORED: 'itemRestored',
  RESERVATION_ATTACHED: 'reservationAttached',
  SERVICE_REQUESTED: 'serviceRequested',
  SERVICE_RESOLVED: 'serviceResolved',
  BILL_REQUESTED: 'billRequested',
  PAYMENT_TAKEN: 'paymentTaken',
  FORCE_CLOSED: 'forceClosedTimeline',
  MOVED: 'movedTable',
  MERGED_INTO: 'mergedAnother',
  CLOSED: 'tabClosed3',
  SEATED_FROM_RESERVATION: 'seatedReservation',
};

/** Maps a timeline entry's actor type to its `timeline.*` i18n key. */
const ACTOR_LABEL_KEY: Record<
  TableSessionTimelineEntry['actorType'],
  'guest' | 'staff' | 'system'
> = {
  GUEST_QR: 'guest',
  STAFF: 'staff',
  SYSTEM: 'system',
};

/** A session's append-only activity feed - the audit timeline rendered as the
 *  visit's story (oldest first, matching how it accrued). Reachable from the
 *  tab's "Manage" menu and the inspector header; works for open and closed
 *  tabs alike (it's the only place the full history surfaces). */
export default function TableSessionTimelinePage({
  f7route,
}: {
  f7route: { params: { sessionId: string } };
}) {
  const tAll = useT();
  const t = tAll.tableOrdering.timeline;
  const sessionId = f7route.params.sessionId;
  const { data: session, isLoading } = useTableSession(sessionId);
  const entries = session?.timeline ?? [];

  return (
    <Page name="table-session-timeline">
      <Navbar title={t.activity} subtitle={session?.tableLabel} backLink={tAll.back} />

      {isLoading && !session ? (
        <Block style={{ textAlign: 'center', paddingTop: 48 }}>
          <Preloader size={32} />
        </Block>
      ) : entries.length === 0 ? (
        <Block style={{ paddingTop: 32 }}>
          <EmptyState icon="clock" title={t.noActivity} description={t.noActivityDesc} />
        </Block>
      ) : (
        <Block style={{ paddingTop: 8 }}>
          {entries.map((e, i) => {
            const meta = KIND_META[e.kind];
            const label = t[KIND_LABEL_KEY[e.kind]];
            const last = i === entries.length - 1;
            return (
              <div key={e.id} style={{ display: 'flex', gap: 12 }}>
                <div
                  style={{
                    position: 'relative',
                    width: 32,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  {!last && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 30,
                        bottom: -8,
                        left: 15,
                        width: 2,
                        background: 'var(--f7-list-item-border-color, rgba(0,0,0,0.12))',
                      }}
                    />
                  )}
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      background: meta.tint,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                    }}
                  >
                    <i className="f7-icons" style={{ fontSize: 15, color: meta.color }}>
                      {meta.icon}
                    </i>
                  </div>
                </div>
                <div style={{ flex: 1, paddingBottom: last ? 4 : 20, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{label}</div>
                  <div style={{ fontSize: 13, color: '#8e8e93', marginTop: 2 }}>
                    {t[ACTOR_LABEL_KEY[e.actorType]]} ·{' '}
                    {DateTime.fromISO(e.createdAt).toFormat('HH:mm')}
                    {e.note ? ` · ${e.note}` : ''}
                  </div>
                </div>
              </div>
            );
          })}
        </Block>
      )}
    </Page>
  );
}
